import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { APIActionOnAssessmentInvitation, APIDownloadQualificationReportByID, APIGetAssessmentInvitations } from 'utils/api';
import LoadingImage from 'components/LoadingImage';
import { format } from 'date-fns';
import { Popover } from '@mui/material';
import { useAppSelector } from 'store/hooks';
import { downloadBlob } from 'utils/utils';

type StatusType = 'created' | 'sent' | 'opened' | 'in_progress' | 'completed' | 'expired' | 'failed' | 'canceled' | string;

interface StatusTagProps {
  status: StatusType;
}

const StatusTag: React.FC<StatusTagProps> = ({ status }) => {
  let bgColor = 'bg-gray-100';
  let textColor = 'text-gray-800';
  let icon = '🔘';

  let displayedStatus = 'Unknown';

  switch (status?.toLowerCase()) {
    case 'created':
      bgColor = 'bg-indigo-100'; // Example color
      textColor = 'text-indigo-800';
      icon = '📝'; // Example icon
      displayedStatus = 'Created';
      break;
    case 'sent':
      bgColor = 'bg-gray-100';
      textColor = 'text-gray-800';
      icon = '✉️'; // More specific icon
      displayedStatus = 'Sent';
      break;
    case 'opened':
      bgColor = 'bg-blue-100';
      textColor = 'text-blue-800';
      icon = '👀'; // Example icon
      displayedStatus = 'Opened';
      break;
    case 'in_progress': // Use the exact string from backend
    case 'started': // Keep started as alias if needed
      bgColor = 'bg-yellow-100';
      textColor = 'text-yellow-800';
      icon = '⏳'; // Example icon
      displayedStatus = 'In Progress'; // Display friendly text
      break;
    case 'completed':
      bgColor = 'bg-green-100';
      textColor = 'text-green-800';
      icon = '✅';
      displayedStatus = 'Completed';
      break;
    case 'canceled':
      bgColor = 'bg-red-100';
      textColor = 'text-red-800';
      icon = '❌';
      displayedStatus = 'Canceled';
      break;
    case 'expired':
      bgColor = 'bg-orange-100'; // Changed to orange for less severity than failed
      textColor = 'text-orange-800';
      icon = '⏰'; // Clock icon
      displayedStatus = 'Expired';
      break;
    case 'failed':
      bgColor = 'bg-red-100';
      textColor = 'text-red-800';
      icon = '❌';
      displayedStatus = 'Failed';
      break;
    default:
      // Keep default gray for unknown statuses
      bgColor = 'bg-gray-100';
      textColor = 'text-gray-800';
      icon = '❓';
      displayedStatus = status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown'; // Capitalize unknown status if present
      break;
  }

  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${bgColor} ${textColor} custom-text-[poppins-500-12px]`}
    >
      {icon}
      <span>{displayedStatus}</span> {/* Use the processed status text */}
    </span>
  );
};

// --- Avatar Component ---
interface AvatarProps {
  src?: string; // Make src optional
  alt: string;
  name?: string; // Add name for initials fallback
}

const Avatar: React.FC<AvatarProps> = ({ src, alt, name }) => {
  // Basic initials logic
  const initials = name
    ? name.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase()
    : '?';

  return (
    <div className="flex overflow-hidden flex-shrink-0 justify-center items-center w-8 h-8 bg-gray-200 rounded-full">
      {src ? (
        <img src={src} alt={alt} className="object-cover w-full h-full" />
      ) : (
        <span className="text-xs font-medium text-gray-600">{initials}</span>
      )}
    </div>
  );
};


const OrgActivityPage = () => {
  const queryClient = useQueryClient();
  const user = useAppSelector((state) => state.auth.user_me);
  const selectedOrgId = useAppSelector((state) => state.auth.selectedOrganizationId);
  const [selectedInvitation, setSelectedInvitation] = useState<any>(null);

  const { data: invitations, isLoading, error } = useQuery({
    queryKey: ['assessmentInvitations', selectedOrgId],
    queryFn: () => APIGetAssessmentInvitations({ with_lab: true, passed: true, with_user: true, org_id: selectedOrgId }).then(res => res.data),
    enabled: !!selectedOrgId,
  });

  const downloadReportMutation = useMutation({
    mutationFn: (data: any) => {
      return APIDownloadQualificationReportByID(data.id).then(res => ({
        data: res.data,
        headers: res.headers,
      }));
    },
    onSuccess: ({ data, headers }: { data: Blob, headers: Headers }) => {
      try {
        let filename = `report-${selectedInvitation?.user?.firstname}-${selectedInvitation?.user?.lastname}-${format(new Date(selectedInvitation.end_date), 'yyyy-MM-dd-HH-mm')}.pdf`;

        const contentDisposition = headers['content-disposition'];
        if (contentDisposition) {
          const match = contentDisposition.match(/filename="([^"]+)"/);
          if (match && match[1]) {
            filename = match[1];
          }
        }

        downloadBlob(data, filename);
        queryClient.invalidateQueries({ queryKey: ["assessmentInvitations"] });
      } catch (error) {
        console.error("Error handling downloaded report:", error);
      }
    },
    onError: (error: any) => {
      console.error("Error downloading report:", error);
    },
  });

  const actionOnInvitationMutation = useMutation({
    mutationFn: (data: any) => {
      return APIActionOnAssessmentInvitation(data.id, data.action, data.data).then(res => res.data)
    },
    onSuccess: (data, variables) => {
      console.log("Invitation sent successfully:", data);
      queryClient.invalidateQueries({ queryKey: ['assessmentInvitations'] });
    },
    onError: (error: any) => {
      console.error("Error sending invite:", error);
    },
  });

  const [popoverState, setPopoverState] = useState<{ anchorEl: HTMLButtonElement | null; invitationId: string | null }>({
    anchorEl: null,
    invitationId: null,
  });

  const handleOpenPopover = (event: React.MouseEvent<HTMLButtonElement>, invitationId: string) => {
    setPopoverState({ anchorEl: event.currentTarget, invitationId });
  };

  const handleClosePopover = () => {
    setPopoverState({ anchorEl: null, invitationId: null });
  };

  const handleDownloadReport = (invitation: any) => {
    if (invitation._qualification_report_id) {
      setSelectedInvitation(invitation);
      downloadReportMutation.mutate({ id: invitation._qualification_report_id });
    } else {
      console.error("No qualification report ID found for this invitation.");
      // Optionally show an error message to the user
    }
    handleClosePopover();
  };
  const handleResendInvite = (invitation: any) => {
    console.log("Resend Invite:", invitation.id);
    actionOnInvitationMutation.mutate({ id: invitation.id, action: 'resend', data: {} });
    handleClosePopover();
  };
  const handleCancelInvite = (invitation: any) => {
    console.log("Cancel Invite:", invitation.id);
    actionOnInvitationMutation.mutate({ id: invitation.id, action: 'cancel', data: {} });
    handleClosePopover();
  };


  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'dd.MM.yyyy HH:mm');
    } catch {
      return 'Invalid Date';
    }
  };

  const showInvitationActions = (invitation: any) => {
    return invitation.status?.toLowerCase() === 'sent' || invitation.status?.toLowerCase() === 'opened' || (invitation.status?.toLowerCase() === 'completed' && invitation._qualification_report_id)
  }

  // Define grid columns template for consistency
  const gridCols = "grid-cols-[2rem_1.5fr_1.2fr_1fr_1fr_2rem]"; // Fixed width for avatar and actions columns

  return (
    <section className="flex w-full h-full border-l-[1px] border-[#F5F5F5]">
      <div className="flex flex-col pt-[56px] px-[46px] h-full w-full">
        {/* Header */}
        <div className="mb-10 text-[#111827]">
          <h1 className="custom-text-[volkhov-700-35px]">
            Hello {user?.firstname}
          </h1>
          <p className="custom-text-[poppins-400-18px]">
            Here is an overview or current status.
          </p>
        </div>

        {/* Table Card */}
        <div className="bg-white rounded-xl shadow-[0px_7px_29px_0px_rgba(100,100,111,0.20)] overflow-hidden mb-10 px-[23px]">
          {/* Table Header Section */}
          <div className="flex gap-3 items-center py-[30px] px-[23px]">
            {/* Placeholder for back arrow or icon */}
            <img src={require("assets/profile-icon-sidebar.svg").default} alt="Candidates" className="w-5 h-5 opacity-70" />
            <h2 className="custom-text-[poppins-500-18px] text-gray-800">
              Recent candidates <span className="text-gray-400">/ Sent tests</span>
            </h2>
          </div>

          {/* Table Container */}
          <div className="overflow-x-auto">
            <div className="min-w-[900px] lg:min-w-full"> {/* Min width from Anima */}
              {/* Table Header Row */}
              <div className={`grid ${gridCols} px-4 py-2 bg-[#EAF3FF] mx-auto rounded-[9px] h-8`}>
                <div className="custom-text-[poppins-500-12px] text-gray-500 flex items-center justify-center"> </div>
                <div className="custom-text-[poppins-500-12px] text-gray-500 pl-4 flex items-left">Name</div>
                <div className="custom-text-[poppins-500-12px] text-gray-500 pl-[4px] flex items-left">Lab Name</div>
                <div className="custom-text-[poppins-500-12px] text-gray-500 flex items-left gap-1 cursor-pointer hover:text-gray-700">
                  Sent on
                  <img src={require("assets/sort-icon.svg").default} alt="Sort" className="w-1.5 h-2.5 inline-block" />
                </div>
                <div className="custom-text-[poppins-500-12px] text-gray-500 pl-1 flex items-left">Status</div>
                <div className="custom-text-[poppins-500-12px] text-gray-500 flex items-center justify-center"> </div>
              </div>

              {/* Table Body */}
              <div>
                {isLoading && <div className="p-4 text-center"><LoadingImage /></div>}
                {error && <div className="p-4 text-center text-red-500">Error loading activity: {(error as Error).message}</div>}
                {!isLoading && !error && invitations && invitations.length > 0 && (
                  invitations.map((invite: any) => (
                    <div
                      key={invite.id}
                      className={`grid gap-4 items-center px-4 border-t border-gray-100 ${gridCols} hover:bg-gray-50`}
                    >
                      <div className="flex justify-center items-center py-3 w-8 h-8">
                        <Avatar alt={`${invite.user?.firstname || ''} ${invite.user?.lastname || ''}`} name={`${invite.user?.firstname || ''} ${invite.user?.lastname || ''}`} />
                      </div>
                      <div className="py-3 custom-text-[poppins-500-14px] text-gray-800 truncate min-w-0" title={`${invite.user?.firstname || ''} ${invite.user?.lastname || ''}`}>{`${invite.user?.firstname || ''} ${invite.user?.lastname || ''}`}</div>
                      <div className="py-3 custom-text-[poppins-400-14px] text-gray-600 truncate min-w-0" title={invite.lab?.name || 'N/A'}>{invite.lab?.name || 'N/A'}</div>
                      <div className="py-3 custom-text-[poppins-400-14px] text-gray-600">
                        {formatDate(invite.created_at)}
                      </div>
                      <div className="py-3">
                        <StatusTag status={invite.status} />
                      </div>
                      <div className="flex justify-center items-center py-3 w-8 h-8 text-right">
                        {
                          showInvitationActions(invite) ? (
                            <>
                              <button
                                aria-describedby={`action-popover-${invite.id}`}
                                onClick={(e) => handleOpenPopover(e, invite.id)}
                                className="p-1 text-gray-400 hover:text-gray-600"
                              >
                                <img src={require("assets/more-dots-icon.svg").default} alt="More options" className="w-5 h-5" />
                              </button>

                              <Popover
                                id={`action-popover-${invite.id}`}
                                open={popoverState.invitationId === invite.id}
                                anchorEl={popoverState.anchorEl}
                                onClose={handleClosePopover}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                                PaperProps={{ sx: { width: '160px', boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)', borderRadius: '8px', marginTop: '8px' } }}
                              >
                                <div className="py-1">
                                  {/* View Result / Download Report Button */}
                                  {invite.status?.toLowerCase() === 'completed' && invite._qualification_report_id && (
                                    <button onClick={() => handleDownloadReport(invite)} className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left custom-text-[poppins-400-14px]">
                                      {/* Using a download icon, but action navigates */}
                                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 opacity-70">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                                      </svg>
                                      Download Report
                                    </button>
                                  )}

                                  {/* TODO: Conditionally show Resend based on status */}
                                  {(invite.status?.toLowerCase() === 'sent' || invite.status?.toLowerCase() === 'expired') && (
                                    <button onClick={() => handleResendInvite(invite)} className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left custom-text-[poppins-400-14px]">
                                      <img src={require("assets/resend-invite-icon.svg").default} alt="" className="w-4 h-4 opacity-70" /> Resend Invite
                                    </button>
                                  )}
                                  {/* TODO: Conditionally show Cancel based on status */}
                                  {(invite.status?.toLowerCase() === 'sent' || invite.status?.toLowerCase() === 'opened') && (
                                    <>
                                      <hr className="my-1 border-gray-200" />
                                      <button onClick={() => handleCancelInvite(invite)} className="flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 w-full text-left custom-text-[poppins-400-14px]">
                                        <img src={require("assets/cancel-invite-icon.svg").default} alt="" className="w-4 h-4 opacity-70" /> Cancel Invite
                                      </button>
                                    </>
                                  )}
                                </div>
                              </Popover>
                            </>
                          ) : (
                            // placeholder for actions
                            <div className="py-3 text-right w-[28px]">
                            </div>
                          )
                        }
                      </div>
                    </div>
                  ))
                )}
                {!isLoading && !error && (!invitations || invitations.length === 0) && (
                  <div className="p-4 text-center text-gray-500 border-t border-gray-100">No activity found.</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default OrgActivityPage;